import { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    Alert,
    Tabs,
    Tab,
    Tooltip,
    Select,
    useToast,
} from "barte-design-system";
import { CreditCardIcon, InfoIcon } from "@phosphor-icons/react";
import { usePostHog } from '@posthog/react';

import { useDelay } from "@payfac/hooks";
import { getCompanyPlanContext, getCompanyPlanTaxByMcc } from "@payfac/helpers";
import { InputNumber, Modalize } from "@/modules/shared/components";
import { TRACK_EVENTS } from '@payfac/constants';

import { CardNetworkTables } from "./components";

import { initialCreditData, manualValidate, getTabItem, updateTabItem } from './modal-edit-credit.constants'

import { CardInfo } from "@shared/components/card-info";
import { ModalConfirmClearRates } from "../modal-confirm-clear-rates";

import styles from "./modal-edit-credit.module.css";

import type { ModalEditCreditProps } from "./modal-edit-credit.type";
import {
    IRatePlanTaxCredit,
    ERatePlanTaxCapture,
    ERatePlanTaxCreditBrand,
    ERatePlanReceiptType,
} from "@payfac/types";

export const ModalEditCredit = ({
    isOpen,
    onClose,
    data,
    receiptType,
    onHandleSubmit,
    isLoading,
    earlyCompanyPlans,
    timelineCompanyPlans,
    sellerCompanyPlans,
    onChangeReceiptType,
    taxAggregateVersion,
    mcc,
    receiptTypeChanged,
    params,
}: ModalEditCreditProps) => {
    const { t } = useTranslation();
    const toast = useToast();
    const posthog = usePostHog();

    const [dataForm, setDataForm] = useState(data);
    const [initEdit, setInitEdit] = useState(false);
    const [activeTab, setActiveTab] = useState<keyof typeof ERatePlanTaxCapture>(
        ERatePlanTaxCapture.online,
    );
    const [isEditing] = useState(true);
    const [isOpenModalConfirm, setIsOpenModalConfirm] = useState(false);
    const [pendingReceiptType, setPendingReceiptType] = useState<"TIMELINE" | "EARLY" | null>(null);

    const [showErrors, setShowErrors] = useState(false);

    const [pendingDelayForRedeem, setPendingDelayForRedeem] = useState<{
        type: keyof typeof ERatePlanTaxCapture;
        value: number;
        oldValue: number | null;
    } | null>(null);
    const pendingDelayRef = useRef(pendingDelayForRedeem);
    const [isOpenModalConfirmDelay, setIsOpenModalConfirmDelay] = useState(false);


    useEffect(() => {
        pendingDelayRef.current = pendingDelayForRedeem;
    }, [pendingDelayForRedeem]);

    const handleDelayTimeout = () => {
        const pending = pendingDelayRef.current;

        if (!pending) return;

        const { type, value } = pending;

        const originalDelay = getTabItem(data, type)?.delayForRedeem;
        const isRestoringOriginal = originalDelay !== undefined && originalDelay !== null && originalDelay === value;

        if (isRestoringOriginal) {
            toast.info({ title: t("payfac.pageRatePlanModalEditCreditDelayRestoreInfo") });

            if (dataForm) {
                const newData = JSON.parse(JSON.stringify([...dataForm]));

                const originalTaxData = getTabItem(data, type);

                const idx = newData.findIndex((item: any) => item?.[type] !== undefined);
                const newTaxData = idx !== -1 ? newData[idx]?.[type] : null;

                newTaxData.delayForRedeem = originalTaxData?.delayForRedeem;

                if (originalTaxData && newTaxData) {
                    for (const networkKey in originalTaxData) {
                        if (networkKey !== 'delayForRedeem') {
                            newTaxData[networkKey] = JSON.parse(JSON.stringify(originalTaxData[networkKey]));
                        }
                    }
                }

                setDataForm(newData);
            }

            posthog.capture(TRACK_EVENTS.RATE_PLAN.DELAY_FOR_REDEEM_CONFIRM_CLICKED, {
                type,
                value,
            });

            setPendingDelayForRedeem(null);
        } else {
            setIsOpenModalConfirmDelay(true);
        }
    };

    const { startDelay, clearDelay } = useDelay(handleDelayTimeout, 2000);

    const handleRateChange = (
        itemIndex: number,
        networkKey: string,
        rateIndex: number,
        newValue: number,
    ) => {
        if (!dataForm) return;

        const newData = [...dataForm];
        const currentItem = newData[itemIndex];

        if (!currentItem || !currentItem[activeTab]) return;

        const tabData = { ...currentItem[activeTab] };

        if (!tabData[networkKey]) {
            const defaultRates =
                tabData.default?.sellerRates || Array(24).fill(null);
            tabData[networkKey] = { sellerRates: [...defaultRates] };
        }

        if (tabData[networkKey] && tabData[networkKey].sellerRates) {
            const newRates = [...tabData[networkKey].sellerRates];
            newRates[rateIndex] = newValue;
            tabData[networkKey] = {
                ...tabData[networkKey],
                sellerRates: newRates,
            };
        }

        newData[itemIndex] = {
            ...currentItem,
            [activeTab]: tabData,
        };

        setDataForm(newData);
    };

    const handleRemoveNetwork = (itemIndex: number, networkKey: string) => {
        if (!dataForm) return;

        const newData = [...dataForm];
        const currentItem = newData[itemIndex];

        if (!currentItem || !currentItem[activeTab]) return;

        const tabData = { ...currentItem[activeTab] } as any;
        delete tabData[networkKey];

        newData[itemIndex] = {
            ...currentItem,
            [activeTab]: tabData,
        };

        setDataForm(newData);
    };

    const applyReceiptTypeChange = (value: "TIMELINE" | "EARLY", keepRates = false) => {
        const deepCopy: IRatePlanTaxCredit[] = JSON.parse(
            JSON.stringify(dataForm?.length ? dataForm : initialCreditData)
        );

        const delayValue = value === "TIMELINE" ? 30 : null;

        const resetRates = (tabData: any) => {
            if (keepRates) return tabData;
            const updated = { ...tabData };
            for (const networkKey in updated) {
                if (networkKey !== 'delayForRedeem' && updated[networkKey]?.sellerRates) {
                    updated[networkKey] = { ...updated[networkKey], sellerRates: Array(24).fill(null) };
                }
            }
            return updated;
        };

        let result = updateTabItem(deepCopy, ERatePlanTaxCapture.online, resetRates({ ...getTabItem(deepCopy, ERatePlanTaxCapture.online), delayForRedeem: delayValue }));
        result = updateTabItem(result, ERatePlanTaxCapture.pos, resetRates({ ...getTabItem(result, ERatePlanTaxCapture.pos), delayForRedeem: delayValue }));
        result = updateTabItem(result, ERatePlanTaxCapture.tap, resetRates({ ...getTabItem(result, ERatePlanTaxCapture.tap), delayForRedeem: delayValue }));

        if (value === "EARLY") {
            setShowErrors(false);
        }

        setDataForm(result);
        onChangeReceiptType?.(value);
    };

    const onHandleReceiptTypeChange = (value: keyof typeof ERatePlanReceiptType) => {
        if (value === receiptType) return;

        setPendingReceiptType(value);
        setIsOpenModalConfirm(true);
        posthog.capture(TRACK_EVENTS.RATE_PLAN.RECEIPT_TYPE_CHANGE_CLICKED, { from: receiptType, to: value });
    };

    const onConfirmReceiptTypeChange = () => {
        if (pendingReceiptType) {
            applyReceiptTypeChange(pendingReceiptType);
        }

        setPendingReceiptType(null);
        setIsOpenModalConfirm(false);
        posthog.capture(TRACK_EVENTS.RATE_PLAN.RECEIPT_TYPE_CONFIRM_CLICKED, { receipt_type: pendingReceiptType });
    };

    const onCancelReceiptTypeChange = () => {
        setPendingReceiptType(null);
        setIsOpenModalConfirm(false);
    };

    const onKeepRatesReceiptType = () => {
        if (pendingReceiptType) {
            applyReceiptTypeChange(pendingReceiptType, true);
        }

        setPendingReceiptType(null);
        setIsOpenModalConfirm(false);
        posthog.capture(TRACK_EVENTS.RATE_PLAN.RECEIPT_TYPE_CONFIRM_CLICKED, { receipt_type: pendingReceiptType, keep_rates: true });
    };

    const onHandleDelayForReedemChange = (
        type: keyof typeof ERatePlanTaxCapture,
        value: number,
    ) => {
        const oldValue = pendingDelayForRedeem?.oldValue ?? getTabItem(dataForm, type)?.delayForRedeem ?? null;

        if (dataForm) {
            const deepCopy = JSON.parse(JSON.stringify(dataForm));

            const updatedForm = updateTabItem(deepCopy, type, {
                ...getTabItem(deepCopy, type),
                delayForRedeem: value,
            });
            setDataForm(updatedForm);
        }

        setPendingDelayForRedeem({ type, value, oldValue });

        posthog.capture(TRACK_EVENTS.RATE_PLAN.DELAY_FOR_REDEEM_ON_CHANGE, {
            type,
            value,
            old_value: oldValue,
        });

        if (value !== null && value !== undefined) {
            startDelay();
        } else {
            setPendingDelayForRedeem(null);
        }
    };

    const onConfirmDelayForRedeemChange = () => {
        clearDelay();

        if (pendingDelayForRedeem) {
            const { type } = pendingDelayForRedeem;

            posthog.capture(TRACK_EVENTS.RATE_PLAN.DELAY_FOR_REDEEM_CONFIRM_CLICKED, {
                type,
                value: pendingDelayForRedeem.value,
            });

            setActiveTab(type);

            if (dataForm) {
                const current = { ...getTabItem(dataForm, type) };
                for (const networkKey in current) {
                    if (networkKey !== 'delayForRedeem' && current[networkKey]?.sellerRates) {
                        current[networkKey] = { ...current[networkKey], sellerRates: Array(24).fill(null) };
                    }
                }
                setDataForm(updateTabItem(dataForm, type, current));
            }
        }

        setPendingDelayForRedeem(null);
        setIsOpenModalConfirmDelay(false);
    };

    const onCancelDelayForRedeemChange = () => {
        clearDelay();
        if (pendingDelayForRedeem) {
            const { type, oldValue } = pendingDelayForRedeem;

            if (dataForm) {
                const deepCopy = JSON.parse(JSON.stringify(dataForm));

                const updatedForm = updateTabItem(deepCopy, type, {
                    ...getTabItem(deepCopy, type),
                    delayForRedeem: oldValue,
                });

                setDataForm(updatedForm);
            }

            posthog.capture(TRACK_EVENTS.RATE_PLAN.DELAY_FOR_REDEEM_CANCEL_CLICKED, {
                type,
                value: pendingDelayForRedeem.value,
                reverted_to: oldValue,
            });
        }

        setPendingDelayForRedeem(null);
        setIsOpenModalConfirmDelay(false);
    };

    const onKeepRatesDelay = () => {
        if (pendingDelayForRedeem) {
            const { type, value } = pendingDelayForRedeem;

            posthog.capture(TRACK_EVENTS.RATE_PLAN.DELAY_FOR_REDEEM_CONFIRM_CLICKED, {
                type,
                value,
                keep_rates: true,
            });

            setPendingDelayForRedeem(null);
            setIsOpenModalConfirmDelay(false);
            clearDelay();
            toast.success({ title: t("payfac.pageRatePlanModalEditCreditDelayRestoreInfo") });
        }
    };

    const getCreditPlansByDelay = (
        capture: keyof typeof ERatePlanTaxCapture,
    ) => {
        const delayForRedeem = getTabItem(dataForm, capture)?.delayForRedeem;
        const context = getCompanyPlanContext({
            sellerCompanyPlans,
            earlyCompanyPlans,
            timelineCompanyPlans,
            receiptType,
            receiptTypeChanged,
        });
        const creditCard = getCompanyPlanTaxByMcc(context, mcc)?.creditCard;
        return creditCard?.[delayForRedeem] || creditCard?.default;
    };

    const getCompanyPlan = (
        network: keyof typeof ERatePlanTaxCreditBrand,
    ) => {
        const companyPlansRates = getCreditPlansByDelay(activeTab)?.[activeTab];
        return companyPlansRates?.[network as ERatePlanTaxCreditBrand] || companyPlansRates?.default;
    };

    const getCompanyNetworksByType = (
        type: keyof typeof ERatePlanTaxCapture,
    ) => Object.keys(getCreditPlansByDelay(type)?.[type] || {});

    const hasCompanyPlan = useCallback(
        (param: keyof typeof ERatePlanTaxCapture) => {
            return !!getCreditPlansByDelay(param)?.[param];
        },
        [dataForm, sellerCompanyPlans, earlyCompanyPlans, timelineCompanyPlans, receiptType, receiptTypeChanged, mcc]
    );

    const hasDelayForReedemError = useCallback(
        (param: keyof typeof ERatePlanTaxCapture) => {
            if (!hasCompanyPlan(param)) return true;

            const value = getTabItem(dataForm, param)?.delayForRedeem;
            if (showErrors && (value === undefined || value === null)) return true;

            return false;
        },
        [showErrors, dataForm, hasCompanyPlan]
    );

    const getDelayForReedemHelperText = useCallback(
        (param: keyof typeof ERatePlanTaxCapture) => {
            if (!hasCompanyPlan(param)) {
                return t("payfac.pageRatePlanModalEditCreditNoHasCompanyPlanHelperText");
            }

            const value = getTabItem(dataForm, param)?.delayForRedeem;
            if (showErrors && (value === undefined || value === null)) {
                return t("payfac.pageRatePlanModalRequiredField");
            }

            return "";
        },
        [showErrors, dataForm, hasCompanyPlan, t]
    );

    const tabsProps = {
        data: dataForm,
        isLoading,
        isEditing,
        onChange: handleRateChange,
        onRemoveNetwork: handleRemoveNetwork,
        getCompanyPlan,
        hasCompanyPlan,
        showErrors,
        taxAggregateVersion
    };

    const tabs = {
        [ERatePlanTaxCapture.online]: {
            label: t("payfac.pageRatePlanModalTabOnline"),
            content: <CardNetworkTables {...tabsProps} type={ERatePlanTaxCapture.online} companyNetworks={getCompanyNetworksByType(ERatePlanTaxCapture.online)} />,
        },
        [ERatePlanTaxCapture.pos]: {
            label: t("payfac.pageRatePlanModalTabPos"),
            content: <CardNetworkTables {...tabsProps} type={ERatePlanTaxCapture.pos} companyNetworks={getCompanyNetworksByType(ERatePlanTaxCapture.pos)} />,
        },
        [ERatePlanTaxCapture.tap]: {
            label: t("payfac.pageRatePlanModalTabTap"),
            content: <CardNetworkTables {...tabsProps} type={ERatePlanTaxCapture.tap} companyNetworks={getCompanyNetworksByType(ERatePlanTaxCapture.tap)} />,
        },
    };

    const title = (
        <div className={styles["modal-header"]}>
            <CreditCardIcon /> {t("payfac.pageRatePlanModalEditCreditTitle")}
        </div>
    ) as unknown as string;

    const hasDiff =
        JSON.stringify(dataForm) !== JSON.stringify(data) ||
        receiptTypeChanged;


    const handleSave = () => {
        const { isValid, tab } = manualValidate(dataForm);

        if (!isValid) {
            setShowErrors(true);

            toast.error({ title: t("payfac.pageRatePlanModalRequiredFields") });

            if (tab) {
                setActiveTab(tab);
            }

            return;
        }

        onHandleSubmit(dataForm as IRatePlanTaxCredit[], receiptType);
        posthog.capture(TRACK_EVENTS.RATE_PLAN.CREDIT_CLICKED, { receipt_type: receiptType });
    };


    useEffect(() => {
        if (isOpen) {
            if (!initEdit) {
                const deepCopy = JSON.parse(JSON.stringify(data));

                setDataForm(deepCopy);
                setInitEdit(true);
            }
        } else {
            setDataForm({} as ModalEditCreditProps["data"]);
            setInitEdit(false);
        }
    }, [isOpen, data, initEdit]);


    return (
        <>
            <Modalize
                isOpen={isOpen}
                onClose={onClose}
                title={title}
                footer={{
                    primaryButton: {
                        children: t("payfac.pageRatePlanModalSaveButton"),
                        onClick: handleSave,
                        disabled: !hasDiff,
                        loading: isLoading,
                    },
                    secondaryButton: {
                        children: t("payfac.pageRatePlanModalCancelButton"),
                        variant: "outline",
                        onClick: onClose,
                        disabled: isLoading,
                    },
                    buttonLayout: "hug",
                }}
            >
                <div className={styles.content}>
                    {params}

                    <CardInfo
                        title={null}
                        info={[
                            {
                                label: (
                                    <div className={styles['modal-header']}>
                                        {t("payfac.pageRatePlanDetailsReceiptType")}
                                        <Tooltip placement="bottom" label={t("payfac.pageRatePlanModalRepassTimeTitleTooltip")}>
                                            <InfoIcon size={16} />
                                        </Tooltip>
                                    </div>
                                ) as unknown as string,
                                value: (
                                    <Select
                                        value={receiptType}
                                        onChange={(e) => onHandleReceiptTypeChange(e as keyof typeof ERatePlanReceiptType)}
                                        options={[
                                            { icon: null, value: "TIMELINE", label: t("payfac.pageRatePlanModalRepassTimeFlow") },
                                            { icon: null, value: "EARLY", label: t("payfac.pageRatePlanModalRepassTimeAnticipated") }
                                        ]}
                                        hidePrefixIcon
                                    />
                                )
                            },
                            {
                                label: t("payfac.pageRatePlanDetailsRedeemTimeOnline"),
                                value: (
                                    <InputNumber
                                        value={getTabItem(dataForm, ERatePlanTaxCapture.online)?.delayForRedeem}
                                        onChange={(value) =>
                                            onHandleDelayForReedemChange(ERatePlanTaxCapture.online, value)
                                        }
                                        prefix="D + "
                                        className={styles["repass-input-number"]}
                                        decimalPrecision={0}
                                        disabled={receiptType === "TIMELINE"}
                                        error={hasDelayForReedemError(ERatePlanTaxCapture.online)}
                                        helperText={getDelayForReedemHelperText(ERatePlanTaxCapture.online)}
                                    />
                                )
                            },
                            {
                                label: t("payfac.pageRatePlanDetailsRedeemTimePos"),
                                value: (
                                    <InputNumber
                                        value={getTabItem(dataForm, ERatePlanTaxCapture.pos)?.delayForRedeem}
                                        onChange={(value) => onHandleDelayForReedemChange(ERatePlanTaxCapture.pos, value)}
                                        prefix="D + "
                                        className={styles["repass-input-number"]}
                                        decimalPrecision={0}
                                        disabled={receiptType === "TIMELINE"}
                                        error={hasDelayForReedemError(ERatePlanTaxCapture.pos)}
                                        helperText={getDelayForReedemHelperText(ERatePlanTaxCapture.pos)}
                                    />
                                )
                            },
                            {
                                label: t("payfac.pageRatePlanDetailsRedeemTimeTap"),
                                value: (
                                    <InputNumber
                                        value={getTabItem(dataForm, ERatePlanTaxCapture.tap)?.delayForRedeem}
                                        onChange={(value) => onHandleDelayForReedemChange(ERatePlanTaxCapture.tap, value)}
                                        prefix="D + "
                                        className={styles["repass-input-number"]}
                                        decimalPrecision={0}
                                        disabled={receiptType === "TIMELINE"}
                                        error={hasDelayForReedemError(ERatePlanTaxCapture.tap)}
                                        helperText={getDelayForReedemHelperText(ERatePlanTaxCapture.tap)}
                                    />
                                )
                            }
                        ]}
                    />

                    <div className={styles.tabs}>
                        <div className={styles["tabs-row"]}>
                            <Tabs>
                                {Object.entries(tabs).map(([key, value]) => (
                                    <Tab
                                        key={key}
                                        active={activeTab === key}
                                        onClick={() => setActiveTab(key as keyof typeof ERatePlanTaxCapture)}
                                    >
                                        {value.label}
                                    </Tab>
                                ))}
                            </Tabs>
                        </div>

                        {!hasCompanyPlan(activeTab) ? (
                            <Alert
                                title={t("payfac.pageRatePlanModalEditCreditNoHasCompanyPlanTitle")}
                                description={t("payfac.pageRatePlanModalEditCreditNoHasCompanyPlanDescription")}
                                variant="error"
                                showCloseButton={false}
                            />
                        ) : ''}

                        {tabs[activeTab]?.content}
                    </div>
                </div>
            </Modalize>

            <ModalConfirmClearRates
                isOpen={isOpenModalConfirm}
                onClose={onCancelReceiptTypeChange}
                onConfirm={onConfirmReceiptTypeChange}
                onKeepRates={onKeepRatesReceiptType}
                titleKey="payfac.pageRatePlanModalConfirmReceiptTypeTitle"
                descriptionKey="payfac.pageRatePlanModalConfirmReceiptTypeDescription"
            />

            <ModalConfirmClearRates
                isOpen={isOpenModalConfirmDelay}
                onClose={onCancelDelayForRedeemChange}
                onConfirm={onConfirmDelayForRedeemChange}
                onKeepRates={onKeepRatesDelay}
                titleKey="payfac.pageRatePlanModalConfirmDelayChangeTitle"
                descriptionKey="payfac.pageRatePlanModalConfirmDelayChangeDescription"
            />
        </>
    );
};
